import { createBrowserRouter, Navigate } from "react-router-dom";
import { AppLayout } from "@/components/layout/app-layout";
import { RouteErrorScreen } from "@/components/errors/RouteErrorScreen";
import { LoginPage } from "@/pages/login";
import { RegisterPage } from "@/pages/register";
import { SignupPage } from "@/pages/signup";
import { VerifyEmailPage } from "@/pages/verify-email";
import { OAuthCallbackPage } from "@/pages/oauth-callback";
import { ActivatePage } from "@/pages/activate";
import { PrivacyPage, TermsPage } from "@/pages/legal";
import { WelcomePage } from "@/pages/welcome";
import { InboxPage } from "@/pages/inbox";
import { EpicDetailPage } from "@/pages/epics/epic-detail";
import { EpicRequestDetailPage } from "@/pages/epic-requests/epic-request-detail";
import { PullRequestReviewPage } from "@/pages/pull-requests/pull-request-review";
import { MailboxJobDetailPage } from "@/pages/mailbox/job-detail";
import { FeatureDetail } from "@/components/features/feature-detail";
import { TaskDetail } from "@/components/tasks/task-detail";
import { TeamDetailPage } from "@/pages/teams/team-detail";
import { GitHubCallbackPage } from "@/pages/github-callback";
import { SlackLinkPage } from "@/pages/slack-link";
import { GoogleCloudCallbackPage } from "@/pages/google-cloud-callback";
import { ThemePreviewPage } from "@/pages/theme-preview";
import { OrganizationMembersPage } from "@/pages/organization/members";
import { BillingPage } from "@/pages/billing";
import { WhatsNewPage } from "@/pages/whats-new";
import { AdminGuard } from "@/components/guards/AdminGuard";
import { AdminUsersPage } from "@/pages/admin/UsersPage";
import { UserReportPage } from "@/pages/admin/UserReportPage";
import { ActivityDashboardPage } from "@/pages/admin/ActivityDashboardPage";
import { ActivityPage } from "@/pages/activity";
import { NotFoundPage } from "@/pages/not-found";

import { ErrorScreen } from "@/components/errors/ErrorScreen";
import { ConnectComputerPage } from "@/pages/connect";
import { DevicesPage } from "@/pages/coming-soon";
import { ConnectionsPage } from "@/pages/connections";
import { GoogleCloudWizardPage } from "@/pages/google-cloud-wizard";
import { AllProjectsPage } from "@/pages/projects/all-projects";
import { newLookRoutes, rebuiltOldRoutes } from "@/components/new-look/routes";

export const router = createBrowserRouter([
  // Preview routes — dev only, tree-shaken in production builds
  ...(import.meta.env.DEV
    ? [
        {
          path: "/preview/error",
          element: (
            <ErrorScreen
              error={Object.assign(new Error("Something went wrong while loading the dashboard component. This is a preview of the error experience."), { name: "RenderError" })}
              statusCode={500}
            />
          ),
        },
        {
          path: "/preview/404",
          element: <NotFoundPage />,
        },
      ]
    : []),
  {
    path: "/login",
    element: <LoginPage />,
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/register",
    element: <RegisterPage />,
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/activate",
    element: <ActivatePage />,
    errorElement: <RouteErrorScreen />,
  },
  // Self-serve signup (public; outside AppLayout so its auth redirect never fires).
  {
    path: "/signup",
    element: <SignupPage />,
    errorElement: <RouteErrorScreen />,
  },
  // First-run onboarding (signed in, but outside AppLayout so its onboarding
  // redirect never targets itself).
  {
    path: "/welcome",
    element: <WelcomePage />,
    errorElement: <RouteErrorScreen />,
  },
  // GitHub sends admins back here after installing the CompBox app. Outside
  // AppLayout so it also works mid-onboarding (the "Connect GitHub" step).
  {
    path: "/settings/github/callback",
    element: <GitHubCallbackPage />,
    errorElement: <RouteErrorScreen />,
  },
  // Google sends admins back here after approving the one-click Google Cloud setup.
  {
    path: "/settings/cloud/google/callback",
    element: <GoogleCloudCallbackPage />,
    errorElement: <RouteErrorScreen />,
  },
  // Legal pages (public; Google's OAuth brand verification links to them).
  {
    path: "/privacy",
    element: <PrivacyPage />,
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/terms",
    element: <TermsPage />,
    errorElement: <RouteErrorScreen />,
  },
  // Slack's one-time account link (ENG-411); handles signed-out visitors itself.
  {
    path: "/slack/link",
    element: <SlackLinkPage />,
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/verify-email",
    element: <VerifyEmailPage />,
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/auth/google/callback",
    element: <OAuthCallbackPage provider="google" />,
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/auth/microsoft/callback",
    element: <OAuthCallbackPage provider="microsoft" />,
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/",
    element: <AppLayout />,
    errorElement: <RouteErrorScreen />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: "inbox", element: <InboxPage /> },
      { path: "activity", element: <ActivityPage /> },
      { path: "whats-new", element: <WhatsNewPage /> },
      // New-look addresses for ideas and blueprints (same pages as below).
      ...newLookRoutes,
      ...rebuiltOldRoutes,
      { path: "epic-requests/:requestId", element: <EpicRequestDetailPage /> },
      { path: "mailbox/jobs/:jobId", element: <MailboxJobDetailPage /> },
      { path: "pull-requests/:id", element: <PullRequestReviewPage /> },
      {
        path: "epics/:epicId",
        element: <EpicDetailPage />,
      },
      {
        path: "features/:featureId",
        element: <FeatureDetail />,
      },
      {
        path: "tasks/:taskId",
        element: <TaskDetail />,
      },
      { path: "teams/:teamId", element: <TeamDetailPage /> },
      // New-look menu items whose pages come later (docs/new-look/PLAN.md).
      { path: "devices", element: <DevicesPage /> },
      { path: "connections", element: <ConnectionsPage /> },
      { path: "connections/google-cloud/*", element: <GoogleCloudWizardPage /> },
      { path: "projects", element: <AllProjectsPage /> },
      // Install the CLI and connect this computer (bring your own AI).
      { path: "connect", element: <ConnectComputerPage /> },
      // Stakeholder preview of the proposed orange rebrand (ENG-222). Scoped
      // CSS variables only — the global theme is unaffected.
      { path: "theme-preview", element: <ThemePreviewPage /> },
      // Organization-admin members screen (ENG-211). Server-side the routes
      // require org-admin authority; a plain member reaching this URL gets
      // the API's 403 message rendered by the page rather than a blank table.
      { path: "organization/members", element: <OrganizationMembersPage /> },
      // Must stay inside the authenticated layout but reachable when the
      // subscription has lapsed — BillingOnlyGuard exempts this path so a
      // blocked customer lands here rather than looping (ENG-200).
      { path: "billing", element: <BillingPage /> },
      {
        path: "admin",
        element: <AdminGuard />,
        children: [
          { index: true, element: <Navigate to="/admin/users" replace /> },
          { path: "users", element: <AdminUsersPage /> },
          { path: "activity/user/:userId", element: <UserReportPage /> },
          { path: "activity", element: <ActivityDashboardPage /> },
        ],
      },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
